import React from 'react';
import { Chip } from '@material-tailwind/react';
import { CalendarIcon, ExternalLinkIcon, UserIcon, XIcon } from 'lucide-react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { URLS } from '../../router-x/urls';
import { OCCUPANCY_COLORS, getEntityLabel } from './constants';

const EntityDetailsPanel = ({ shape, onClose }) => {
  const navigate = useNavigate();
  if (!shape) return null;

  const occupancy = shape.occupancy;
  const statusMeta = OCCUPANCY_COLORS[shape.occupancyStatus] || null;
  const entity = occupancy?.entity;
  const occupant = occupancy?.occupant;
  const contract = occupancy?.contract;
  const booking = occupancy?.booking;
  const dayPass = occupancy?.dayPass;
  const code = entity?.code || entity?.seatCode || getEntityLabel(shape);
  const customerId = occupant?._id;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3">
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-gray-900">{code}</p>
          <p className="text-[11px] capitalize text-gray-400">
            {shape.elementType?.replace(/_/g, ' ')}
          </p>
        </div>
        <button type="button" onClick={onClose} className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-800">
          <XIcon size={14} />
        </button>
      </div>

      {statusMeta && (
        <div className="mb-3">
          <Chip size="sm" variant="ghost" color={statusMeta.chip} value={statusMeta.label} className="rounded-full" />
        </div>
      )}

      {occupant && (
        <div className="mb-3 border-b border-gray-100 pb-3">
          <div className="mb-1 flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-gray-400">
            <UserIcon size={12} /> Occupant
          </div>
          <p className="text-xs text-gray-800">{occupant.name}</p>
          {occupant.email && <p className="text-[11px] text-gray-500">{occupant.email}</p>}
        </div>
      )}

      {contract && (
        <div className="mb-3 border-b border-gray-100 pb-3">
          <div className="mb-1 flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-gray-400">
            <CalendarIcon size={12} /> Contract
          </div>
          <p className="text-xs text-gray-700">
            {moment(contract.startDate).format('DD MMM YYYY')}
            {' → '}
            {contract.endDate ? moment(contract.endDate).format('DD MMM YYYY') : 'Open'}
          </p>
        </div>
      )}

      {booking && (
        <div className="mb-3 border-b border-gray-100 pb-3">
          <p className="mb-1 text-[11px] uppercase tracking-wide text-gray-400">Rent</p>
          <p className="text-xs text-gray-800">
            {booking.rentCurrency || 'PKR'} {booking.rentRate ?? booking.rentTotal ?? '—'}
            {booking.rentFrequency ? ` / ${booking.rentFrequency}` : ''}
          </p>
        </div>
      )}

      {dayPass && (
        <div className="mb-3 border-b border-gray-100 pb-3">
          <p className="mb-1 text-[11px] uppercase tracking-wide text-gray-400">Day pass</p>
          <p className="text-xs text-gray-800">{dayPass.customerName}</p>
          <p className="text-[11px] text-gray-500">
            {moment(dayPass.startTime).format('HH:mm')} – {moment(dayPass.endTime).format('HH:mm')}
          </p>
        </div>
      )}

      {!occupant && !booking && !dayPass && (
        <p className="mb-3 text-xs text-gray-400">No active occupant.</p>
      )}

      <div className="flex flex-wrap gap-2">
        {['office', 'seat'].includes(shape.elementType) && (
          <button
            type="button"
            className="h-7 rounded-full bg-gray-900 px-2.5 text-[11px] text-white hover:bg-gray-800"
            onClick={() => navigate(URLS.ADMIN.BOOKING)}
          >
            Book
          </button>
        )}
        {customerId && (
          <button
            type="button"
            className="inline-flex h-7 items-center gap-1 rounded-full border border-gray-200 px-2.5 text-[11px] text-gray-700 hover:bg-gray-50"
            onClick={() => navigate(`${URLS.ADMIN.CUSTOMER}/${customerId}`)}
          >
            Customer <ExternalLinkIcon size={11} />
          </button>
        )}
      </div>
    </div>
  );
};

export default EntityDetailsPanel;
